import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { CheckCircle2, AlertTriangle, Phone, Clock, ArrowRight } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { cn } from "@/lib/utils"
import { TerminlinksBlock } from "@/components/portal/TerminlinkDialog"

/**
 * Rueckmeldungen der Kunden auf die Post-Call-SMS.
 *
 * Der Kunde bestaetigt oder korrigiert seine Daten ueber den Token-Link
 * (/c/{token}). Korrekturen landen hier zur Freigabe statt direkt in HERO —
 * das Buero ist die pruefende Instanz, und ohne diesen Riegel koennte jeder
 * mit dem Link Stammdaten aendern.
 *
 * Die Uebernahme laeuft bewusst in ZWEI Schritten:
 *   1. PATCH /api/v1/contacts/{id} mit dem vom Backend geliefertem patch_payload
 *   2. POST /api/v1/customer-confirmations/{id}/applied
 * Schritt 1 ist der bestehende, erprobte Schreibpfad (kennt die Provider-Weiche
 * HERO vs. fortytools). Schritt 2 markiert nur. Schlaegt 1 fehl, wird NICHT
 * markiert — sonst gaelte eine Korrektur als uebernommen, die nie ankam.
 */

type Diff = { feld: string; vorher: string; nachher: string }

type Confirmation = {
  id: number
  tenant_slug: string
  status: "pending" | "confirmed" | "corrected" | "applied"
  call_id: string | null
  hero_contact_id: number | null
  hero_project_id: number | null
  phone: string | null
  sms_status: string | null
  sms_error: string | null
  snapshot: Record<string, string>
  response: Record<string, string>
  changed_fields: string[]
  diff: Diff[]
  patch_payload: Record<string, string> | null
  created_at: string | null
  responded_at: string | null
  applied_at: string | null
  applied_by: string | null
}

type ListResp = { items: Confirmation[]; count: number }

const FELD_LABEL: Record<string, string> = {
  first_name: "Vorname",
  last_name: "Nachname",
  street: "Straße",
  zipcode: "PLZ",
  city: "Ort",
  email: "E-Mail",
  phone_mobile: "Mobilnummer",
}

const FILTER = [
  { value: "corrected", label: "Zu prüfen" },
  { value: "confirmed", label: "Bestätigt" },
  { value: "pending", label: "Keine Antwort" },
  { value: "applied", label: "Übernommen" },
  { value: "", label: "Alle" },
]

/**
 * Uebersetzt Backend-/HERO-Fehler in etwas, das im Buero verwertbar ist.
 * Roh durchgereicht sieht das sonst so aus:
 *   HTTP 500: {"error":"HERO API Fehler: 502: {'message': 'HERO GraphQL Fehler',
 *   'graphql_errors': [{'message': 'Record not found in table `customers`.' ...
 * — technisch korrekt, aber niemand weiss, was zu tun ist.
 */
async function lesbarerFehler(res: Response): Promise<string> {
  let roh = ""
  try { roh = await res.text() } catch { /* egal */ }

  if (/Record not found in table .?customers/i.test(roh)) {
    return "Dieser Kontakt existiert in HERO nicht (mehr). Die Korrektur wurde " +
           "NICHT übernommen — bitte den Kontakt in HERO prüfen."
  }
  if (res.status === 401 || res.status === 403) {
    return "Nicht mehr angemeldet — bitte Seite neu laden."
  }
  if (/_noSpecialChars|noSpecialChars/i.test(roh)) {
    return "HERO lehnt Sonderzeichen ab (z. B. ; < >). Bitte die Eingabe bereinigen."
  }
  // Wenn HERO eine konkrete Meldung liefert, die zeigen — aber nur die.
  const m = roh.match(/'message':\s*'([^']{3,160})'/)
  if (m) return `Übernahme fehlgeschlagen: ${m[1]}`
  return `Übernahme fehlgeschlagen (HTTP ${res.status}). Die Korrektur bleibt offen.`
}

function StatusBadge({ s }: { s: Confirmation["status"] }) {
  const map: Record<string, { txt: string; cls: string }> = {
    corrected: { txt: "Korrektur", cls: "pp-badge-warning" },
    confirmed: { txt: "Bestätigt", cls: "pp-badge-success" },
    applied: { txt: "Übernommen", cls: "pp-badge-success" },
    pending: { txt: "Keine Antwort", cls: "pp-badge-new" },
  }
  const b = map[s] ?? { txt: s, cls: "pp-badge-new" }
  return <span className={cn("pp-badge", b.cls)}>{b.txt}</span>
}

export default function KundenbestaetigungPage() {
  const qc = useQueryClient()
  const [status, setStatus] = useState("corrected")
  // ?tenant= aus der Live-Meldung oben rechts (Terminlink-Rückmeldung) —
  // sonst stünde die Liste auf „alle“ bzw. dem zuletzt gewählten Betrieb.
  const [suche] = useSearchParams()
  const [tenant, setTenant] = useState(suche.get("tenant") || "")
  useEffect(() => {
    const t = suche.get("tenant")
    if (t) setTenant(t)
  }, [suche])
  const [fehler, setFehler] = useState<string | null>(null)

  const params = new URLSearchParams()
  if (status) params.set("status", status)
  if (tenant) params.set("tenant_slug", tenant)
  params.set("limit", "200")

  const { data, isLoading, error, isFetching, refetch } = useQuery<ListResp>({
    queryKey: ["kundenbestaetigung", status, tenant],
    queryFn: async () => {
      const res = await fetch(`/api/v1/customer-confirmations?${params}`, {
        credentials: "include",
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    refetchInterval: 30_000,
  })

  // Tenant-Liste fuers Dropdown — include_test=true, damit test_tenant
  // waehrend der Erprobung sichtbar bleibt.
  const tenantList = useQuery({
    queryKey: ["kundenbestaetigung-tenants"],
    queryFn: async () => {
      const r = await fetch("/api/v1/tenants?include_test=true", { credentials: "include" })
      if (!r.ok) return [] as string[]
      const d = await r.json()
      return Array.isArray(d.items) ? d.items.map((t: { slug: string }) => t.slug) : []
    },
    staleTime: 1000 * 60 * 5,
  })

  const uebernehmen = useMutation({
    mutationFn: async (c: Confirmation) => {
      if (!c.patch_payload || !c.hero_contact_id) {
        throw new Error("Kein Kontakt verknüpft — Übernahme nicht möglich")
      }
      // 1) Schreiben über den bestehenden Kontakt-Endpunkt.
      const patch = await fetch(`/api/v1/contacts/${c.hero_contact_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(c.patch_payload),
      })
      if (!patch.ok) {
        throw new Error(await lesbarerFehler(patch))
      }
      // 2) Erst wenn das geklappt hat, als übernommen markieren.
      const mark = await fetch(`/api/v1/customer-confirmations/${c.id}/applied`, {
        method: "POST",
        credentials: "include",
      })
      if (!mark.ok) throw new Error(`Markieren fehlgeschlagen (HTTP ${mark.status})`)
      return mark.json()
    },
    onSuccess: () => {
      setFehler(null)
      qc.invalidateQueries({ queryKey: ["kundenbestaetigung"] })
    },
    onError: (e: Error) => setFehler(e.message),
  })

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error as Error} />

  const items = data?.items ?? []

  return (
    <div>
      <PageHeader
        title="Kunden-Rückmeldungen"
        count={items.length}
        onRefresh={() => refetch()}
        refreshing={isFetching}
      >
        <select
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          className="text-[12.5px] px-2 py-1.5 rounded border border-border"
          style={{ background: "var(--bg-card, #fff)", width: "auto", minWidth: 150 }}
        >
          <option value="">Alle Tenants</option>
          {((tenantList.data || []) as string[]).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="text-[12.5px] px-2 py-1.5 rounded border border-border"
          style={{ background: "var(--bg-card, #fff)", width: "auto", minWidth: 140 }}
        >
          {FILTER.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </PageHeader>

      <TerminlinksBlock tenant={tenant} />

      {fehler && <ErrorBox error={new Error(fehler)} />}

      {items.length === 0 && (
        <div className="p-6 text-muted-foreground text-sm">
          {status === "corrected"
            ? "Keine offenen Korrekturen — alles geprüft."
            : "Keine Einträge."}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((c) => {
          const name = [c.snapshot?.first_name, c.snapshot?.last_name]
            .filter(Boolean).join(" ") || "Unbekannt"
          const istOffen = c.status === "corrected"
          return (
            <div key={c.id} className="pp-card" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "1.02rem" }}>
                    {name} <StatusBadge s={c.status} />
                  </div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap",
                                fontSize: ".85rem", opacity: .75, marginTop: 4 }}>
                    <span><Phone className="w-3 h-3" style={{ display: "inline" }} /> {c.phone ?? "—"}</span>
                    <span>{c.tenant_slug}</span>
                    {c.responded_at && (
                      <span><Clock className="w-3 h-3" style={{ display: "inline" }} /> {fmtRelative(c.responded_at)}</span>
                    )}
                    {c.sms_status === "dry_run" && (
                      <span title="SMS wurde nicht wirklich versendet (Dry-Run)">
                        SMS: Testmodus
                      </span>
                    )}
                    {c.sms_status === "failed" && (
                      <span style={{ color: "var(--pp-danger, #c0392b)" }}
                            title={c.sms_error ?? ""}>
                        SMS fehlgeschlagen
                      </span>
                    )}
                  </div>
                </div>

                {istOffen && (
                  <button
                    className="pp-btn pp-btn-primary"
                    disabled={uebernehmen.isPending || !c.patch_payload}
                    title={!c.patch_payload
                      ? (c.hero_contact_id
                          ? "Keine Änderungen zum Übernehmen"
                          : "Kein HERO-Kontakt verknüpft — Übernahme nicht möglich")
                      : "Korrigierte Daten nach HERO schreiben"}
                    onClick={() => uebernehmen.mutate(c)}
                  >
                    {uebernehmen.isPending ? "Übernehme …" : "Nach HERO übernehmen"}
                  </button>
                )}
              </div>

              {/* Der eigentliche Inhalt: was hat der Bot falsch verstanden? */}
              {c.diff.length > 0 ? (
                <div style={{ marginTop: 12, display: "flex",
                              flexDirection: "column", gap: 6 }}>
                  {c.diff.map((d) => (
                    <div key={d.feld} style={{ display: "flex", alignItems: "center",
                                               gap: 10, flexWrap: "wrap", fontSize: ".92rem" }}>
                      <span style={{ minWidth: 110, opacity: .7 }}>
                        {FELD_LABEL[d.feld] ?? d.feld}
                      </span>
                      <span style={{ textDecoration: "line-through", opacity: .6 }}>
                        {d.vorher || "—"}
                      </span>
                      <ArrowRight className="w-3 h-3" style={{ opacity: .5 }} />
                      <span style={{ fontWeight: 600 }}>{d.nachher || "—"}</span>
                    </div>
                  ))}
                </div>
              ) : c.status === "confirmed" ? (
                <div style={{ marginTop: 10, fontSize: ".9rem", opacity: .75,
                              display: "flex", alignItems: "center", gap: 6 }}>
                  <CheckCircle2 className="w-4 h-4" />
                  Kunde hat alle Daten als richtig bestätigt.
                </div>
              ) : c.status === "pending" ? (
                <div style={{ marginTop: 10, fontSize: ".9rem", opacity: .6,
                              display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle className="w-4 h-4" />
                  Noch keine Rückmeldung.
                </div>
              ) : null}

              {c.status === "applied" && (
                <div style={{ marginTop: 10, fontSize: ".85rem", opacity: .7 }}>
                  Übernommen von {c.applied_by ?? "?"}
                  {c.applied_at ? ` · ${fmtRelative(c.applied_at)}` : ""}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
